import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    fetchWeatherForecast,
    weatherQueryKeys,
    type WeatherLocation,
    type WeatherRequest
} from './weather-api';
import {
    DEFAULT_PRECIP_THRESHOLD_PERCENT,
    weatherDaysByDate,
    type CalendarWeather,
    type WeatherUnits
} from './weather-rules';

export interface UseCalendarWeatherOptions {
    /**
     * The host's whole answer to "may this run": the calendar module has not disabled it (`disableweather`), the caller can call
     * the route, and a calendar is on screen. The module does not work any of that out itself.
     */
    enabled: boolean;
    /** The shoot location. `null` means no request. */
    location: WeatherLocation | null | undefined;
    units: WeatherUnits;
    /** The calendar module's `weatherprecip`. Absent: the default. */
    precipThreshold?: number;
    /**
     * ROBO-865: how the host's caller reaches the route when it is not the signed-in session — e.g.
     * a guest sends no session and a checkout pass. Absent: the session's own call.
     */
    request?: WeatherRequest;
}

/**
 * The forecast for a calendar, as the `weather` prop both pickers take — or `undefined`, which the
 * pickers draw as exactly today's calendar.
 *
 * ★ WEATHER IS A HINT AND MUST NEVER COST THE CALENDAR ANYTHING (ROBO-846, Behaviour 2):
 * - the query runs beside the availability read and nothing waits for it;
 * - one request per page load per rounded location: `staleTime` and `gcTime` are `Infinity`, there
 *   is no retry, and a failed location is not asked again when a calendar remounts;
 * - every failure — a 401, a 429, a 5xx, a malformed body — is one `console.warn` and no weather.
 */
export function useCalendarWeather({
    enabled,
    location,
    units,
    precipThreshold = DEFAULT_PRECIP_THRESHOLD_PERCENT,
    request
}: UseCalendarWeatherOptions): CalendarWeather | undefined {
    const latitude = location?.latitude;
    const longitude = location?.longitude;
    const hasLocation =
        typeof latitude === 'number' &&
        typeof longitude === 'number' &&
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        // 0,0 is the geocoder's miss marker (ROBO-283), not a place: never spend a billed call on it.
        !(latitude === 0 && longitude === 0);
    const where = { latitude: latitude ?? 0, longitude: longitude ?? 0 };

    // `request` says how to authenticate, not what to fetch: the forecast for a place is the same
    // whoever asks, so it stays out of the key and a place is still fetched once per page load.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    const { data } = useQuery({
        queryKey: weatherQueryKeys.forecast(where),
        queryFn: async () => {
            try {
                return await fetchWeatherForecast(where, request);
            } catch (error) {
                console.warn('Weather forecast unavailable; the calendar shows no weather.', error);
                throw error;
            }
        },
        enabled: enabled && hasLocation,
        staleTime: Infinity,
        gcTime: Infinity,
        retry: false,
        retryOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        throwOnError: false
    });

    return useMemo(
        () =>
            enabled && hasLocation && data
                ? { days: weatherDaysByDate(data), units, precipThreshold }
                : undefined,
        [data, enabled, hasLocation, units, precipThreshold]
    );
}
